import { Step1WritingMetadata } from '../types/step1Writing';
import { modeLabel, venueLabel } from '../utils/step1WritingLabels';
import { STEP1_COVERAGE_TOPICS } from '../config/step1CoverageTopics';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function preserveNewlines(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

export function formatFirstStepAsOfDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const FIRST_STEP_DOCUMENT_STYLES = `
  body {
    font-family: Georgia, "Times New Roman", Times, serif;
    font-size: 12pt;
    line-height: 1.75;
    max-width: 40em;
    margin: 0 auto;
    padding: 1.75rem 0.75rem;
    color: #1a1a1a;
  }
  h1 {
    font-size: 1.65rem;
    font-weight: 600;
    margin: 0 0 0.4rem;
    letter-spacing: -0.01em;
    line-height: 1.25;
  }
  .as-of {
    color: #555;
    font-size: 0.95rem;
    margin: 0 0 2rem;
  }
  .story {
    white-space: pre-wrap;
    font-size: 1.08em;
    line-height: 1.85;
    margin: 0;
  }
  .privacy {
    margin-top: 2.5rem;
    padding: 12px 14px;
    background: #fff8e6;
    border-left: 4px solid #e6c200;
    font-size: 0.9rem;
    line-height: 1.55;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  @media print {
    body { padding: 0.5in 0.35in; max-width: none; }
    .privacy { display: none; }
  }
`;

export interface FirstStepShareHtmlOptions {
  /** ISO timestamp of the member's last edit (payload save or workspace activity). */
  lastEditedAt?: string;
  /** Show the confidential-share notice (share links only). */
  includeShareNotice?: boolean;
}

export function generateFirstStepShareHtml(
  contentText: string,
  options: FirstStepShareHtmlOptions = {}
): string {
  const { lastEditedAt, includeShareNotice = false } = options;
  const asOfDate = formatFirstStepAsOfDate(lastEditedAt);
  const asOfLine = asOfDate ? `<p class="as-of">As of ${escapeHtml(asOfDate)}</p>` : '';
  const privacyBlock = includeShareNotice
    ? `<div class="privacy">
    This link expires in 3 days. This document contains sensitive personal recovery material — please treat it confidentially.
  </div>`
    : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>My First Step</title>
  <style>${FIRST_STEP_DOCUMENT_STYLES}</style>
</head>
<body>
  <h1>My First Step</h1>
  ${asOfLine}
  <div class="story">${preserveNewlines(contentText.trim() || '(No story text included)')}</div>
  ${privacyBlock}
</body>
</html>`;
}

export function renderStep1WritingProgressHtml(metadata: Step1WritingMetadata | undefined): string {
  if (!metadata) return '';

  const lines: string[] = [];
  lines.push(`<h2>First Step writing progress</h2>`);
  if (metadata.mode) {
    lines.push(`<p><strong>Approach:</strong> ${escapeHtml(modeLabel(metadata.mode))}</p>`);
  } else {
    lines.push(`<p><strong>Approach:</strong> Not chosen yet</p>`);
  }

  if (metadata.wordCount && metadata.wordCount > 0) {
    const speak =
      metadata.speakingMinutesEstimate &&
      metadata.speakingMinutesEstimate.min === metadata.speakingMinutesEstimate.max
        ? `~${metadata.speakingMinutesEstimate.min} min aloud`
        : metadata.speakingMinutesEstimate
          ? `~${metadata.speakingMinutesEstimate.min}–${metadata.speakingMinutesEstimate.max} min aloud`
          : '';
    lines.push(
      `<p><strong>Length:</strong> ${metadata.wordCount.toLocaleString()} words${speak ? ` · ${speak}` : ''}</p>`
    );
  }

  if (metadata.lastWorkedAt) {
    const formatted = formatFirstStepAsOfDate(metadata.lastWorkedAt);
    if (formatted) {
      lines.push(`<p><strong>Last worked:</strong> ${escapeHtml(formatted)}</p>`);
    }
  }

  if (metadata.coverageStatus) {
    const uncovered = STEP1_COVERAGE_TOPICS.filter((t) => {
      const s = metadata.coverageStatus![t.id];
      return !s || s === 'not_yet' || s === 'partial';
    });
    if (uncovered.length > 0 && uncovered.length < STEP1_COVERAGE_TOPICS.length) {
      lines.push(
        `<p><strong>Themes still in progress:</strong> ${escapeHtml(uncovered.map((t) => t.label).join('; '))}</p>`
      );
    }
  }

  const plan = metadata.presentationPlan;
  if (plan?.targetDate || plan?.venue) {
    lines.push(`<h3>Presentation plan</h3>`);
    if (plan.targetDate) {
      lines.push(`<p><strong>Target date:</strong> ${escapeHtml(plan.targetDate)}</p>`);
    }
    if (plan.venue) {
      lines.push(`<p><strong>Venue:</strong> ${escapeHtml(venueLabel(plan.venue))}</p>`);
    }
    if (plan.venueNote?.trim()) {
      lines.push(`<p><strong>Venue note:</strong> ${preserveNewlines(plan.venueNote)}</p>`);
    }
  }

  lines.push(
    `<p class="meta"><em>Story text is not included in progress shares. Use "Share First Step story" in the First Step writing workspace to share the full narrative.</em></p>`
  );

  return lines.join('\n');
}
